// CORevisionPanel.jsx
import { useState, useCallback } from "react";
import axios from "axios";
import {
  ClockIcon,
  PlusIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  EyeIcon,
} from "@heroicons/react/20/solid";
import { XMarkIcon } from "@heroicons/react/24/outline";

const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";

const formatCurrency = (num) =>
  Number.isFinite(num)
    ? `$${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "$0.00";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(dateStr));
};

const CreateRevisionModal = ({ open, onClose, onConfirm, isCreating }) => {
  const [label, setLabel] = useState("");
  const [note, setNote] = useState("");

  const handleConfirm = () => {
    onConfirm({ label, note });
    setLabel("");
    setNote("");
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-gray-900/75 flex items-center justify-center z-50">
      <div className="bg-gray-700 p-6 rounded-lg shadow-xl w-full max-w-md border border-gray-600">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-orange-400">
            Save Revision Snapshot
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-200"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <p className="text-sm text-gray-400 mb-4">
          This will create a new named snapshot. All future saves will
          auto-update this snapshot until you create another.
        </p>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Label (optional)
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. After client meeting, Price check v2..."
              className="w-full rounded bg-gray-600 border-gray-500 text-gray-200 text-sm px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Note (optional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="What changed in this version..."
              className="w-full rounded bg-gray-600 border-gray-500 text-gray-200 text-sm px-3 py-2"
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-200 bg-gray-600 rounded hover:bg-gray-500"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isCreating}
            className="px-4 py-2 text-sm text-white bg-orange-500 rounded hover:bg-orange-600 disabled:opacity-50"
          >
            {isCreating ? "Saving..." : "Save Snapshot"}
          </button>
        </div>
      </div>
    </div>
  );
};

const RestoreConfirmModal = ({
  open,
  onClose,
  onConfirm,
  revision,
  isRestoring,
}) => {
  if (!open || !revision) return null;
  return (
    <div className="fixed inset-0 bg-gray-900/75 flex items-center justify-center z-50">
      <div className="bg-gray-700 p-6 rounded-lg shadow-xl w-full max-w-md border border-gray-600">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-orange-400">
            Restore Revision?
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-200"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <p className="text-sm text-gray-300 mb-2">
          This will replace the current items and pricing with:
        </p>
        <div className="bg-gray-800 rounded p-3 mb-4 border border-gray-600">
          <div className="font-medium text-white">
            {revision.label || `Revision R${revision.revisionNumber}`}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {formatDate(revision.createdAt)}
          </div>
          {revision.note && (
            <div className="text-xs text-gray-400 mt-1 italic">
              {revision.note}
            </div>
          )}
          <div className="mt-2 flex gap-4 text-xs">
            <span className="text-green-400">
              Budget: {formatCurrency(revision.totalBudgetCost)}
            </span>
            <span className="text-orange-400">
              Price: {formatCurrency(revision.totalCustomerPrice)}
            </span>
          </div>
        </div>
        <p className="text-xs text-yellow-400 mb-4">
          ⚠ After restore, all future saves will auto-update this revision
          snapshot.
        </p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-200 bg-gray-600 rounded hover:bg-gray-500"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isRestoring}
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
          >
            <ArrowPathIcon className="h-4 w-4" />
            {isRestoring ? "Restoring..." : "Restore This Version"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function CORevisionPanel({
  changeOrderId,
  isApproved,
  activeRevisionIndex,
  onRevisionCreated,
  onRevisionRestored,
  onViewRevision, // NEW: called with full revision object
  showAlert,
  token,
}) {
  const [revisions, setRevisions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [loadingRevisionDetail, setLoadingRevisionDetail] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const loadRevisions = useCallback(async () => {
    if (loading) return;
    try {
      setLoading(true);
      const res = await axios.get(
        `${API_BASE}/changeOrders2/${changeOrderId}/revisions`,
        { headers },
      );
      setRevisions(res.data.revisions || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [changeOrderId, loading]);

  const handleToggle = () => {
    const next = !isExpanded;
    setIsExpanded(next);
    if (next && revisions === null) loadRevisions();
  };

  const handleCreateRevision = async ({ label, note }) => {
    try {
      setIsCreating(true);
      await axios.post(
        `${API_BASE}/changeOrders2/${changeOrderId}/revisions`,
        { label, note },
        { headers },
      );
      await loadRevisions();
      setShowCreateModal(false);
      showAlert(
        "success",
        "Snapshot Saved",
        "New revision created. All future saves will update this snapshot.",
      );
      onRevisionCreated?.();
    } catch (err) {
      showAlert(
        "error",
        "Error",
        err.response?.data?.message || "Failed to create revision",
      );
    } finally {
      setIsCreating(false);
    }
  };

  const handleRestore = async () => {
    if (!restoreTarget) return;
    try {
      setIsRestoring(true);
      await axios.put(
        `${API_BASE}/changeOrders2/${changeOrderId}/revisions/${restoreTarget.revisionNumber}/restore`,
        {},
        { headers },
      );
      setRestoreTarget(null);
      showAlert(
        "success",
        "Restored",
        `Restored to ${restoreTarget.label || `R${restoreTarget.revisionNumber}`}. Future saves will update this snapshot.`,
      );
      onRevisionRestored?.();
    } catch (err) {
      showAlert(
        "error",
        "Error",
        err.response?.data?.message || "Failed to restore",
      );
    } finally {
      setIsRestoring(false);
    }
  };

  const handleViewRevision = async (rev) => {
    if (!onViewRevision) return;
    if (rev.items && rev.items.length > 0) {
      onViewRevision(rev);
      return;
    }
    try {
      setLoadingRevisionDetail(true);
      const res = await axios.get(
        `${API_BASE}/changeOrders2/${changeOrderId}/revisions/${rev.revisionNumber}/full`,
        { headers },
      );
      // Merge list-level metadata (label, note) with full detail
      onViewRevision({
        ...res.data.revision,
        label: res.data.revision.label || rev.label,
        note: res.data.revision.note || rev.note,
        revisionNumber: rev.revisionNumber,
      });
    } catch (err) {
      showAlert("error", "Error", "Failed to load revision details");
    } finally {
      setLoadingRevisionDetail(false);
    }
  };

  return (
    <>
      <CreateRevisionModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onConfirm={handleCreateRevision}
        isCreating={isCreating}
      />
      <RestoreConfirmModal
        open={!!restoreTarget}
        onClose={() => setRestoreTarget(null)}
        onConfirm={handleRestore}
        revision={restoreTarget}
        isRestoring={isRestoring}
      />

      <div className="bg-gray-700 rounded-lg border border-gray-600 overflow-hidden">
        <div
          className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-600/50 transition-colors"
          onClick={handleToggle}
        >
          <div className="flex items-center gap-2">
            <ClockIcon className="h-4 w-4 text-orange-400" />
            <span className="text-sm font-medium text-gray-200">
              Revision History
            </span>
            {revisions !== null && (
              <span className="text-xs bg-gray-600 text-gray-300 px-2 py-0.5 rounded-full">
                {revisions.length} snapshot{revisions.length !== 1 ? "s" : ""}
              </span>
            )}
            {activeRevisionIndex !== null &&
              activeRevisionIndex !== undefined &&
              revisions !== null &&
              revisions[activeRevisionIndex] && (
                <span className="text-xs bg-orange-500/20 text-orange-300 px-2 py-0.5 rounded-full border border-orange-500/30">
                  Active:{" "}
                  {revisions[activeRevisionIndex]?.label ||
                    `R${activeRevisionIndex}`}
                </span>
              )}
          </div>
          <div className="flex items-center gap-2">
            {!isApproved && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCreateModal(true);
                }}
                className="px-3 py-1 text-xs text-white bg-orange-500 rounded hover:bg-orange-600 flex items-center"
              >
                <PlusIcon className="h-3 w-3 mr-1" />
                New Snapshot
              </button>
            )}
            <span className="text-gray-400 text-sm">
              {isExpanded ? "▲" : "▼"}
            </span>
          </div>
        </div>

        {isExpanded && (
          <div className="border-t border-gray-600">
            {loading ? (
              <div className="px-4 py-6 text-center text-gray-400 text-sm">
                Loading revisions...
              </div>
            ) : !revisions || revisions.length === 0 ? (
              <div className="px-4 py-6 text-center text-gray-500 text-sm">
                No snapshots yet. R0 is auto-created on first save.
              </div>
            ) : (
              <div className="divide-y divide-gray-600">
                {[...revisions].reverse().map((rev) => {
                  const isActive = activeRevisionIndex === rev.revisionNumber;
                  return (
                    <div
                      key={rev.revisionNumber}
                      className={`px-4 py-3 flex items-start justify-between gap-3 ${
                        isActive
                          ? "bg-orange-900/20 border-l-2 border-orange-500"
                          : "hover:bg-gray-600/30"
                      } transition-colors`}
                    >
                      <div className="flex items-start gap-3 min-w-0">
                        <div
                          className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                            isActive
                              ? "bg-orange-500 text-white"
                              : "bg-gray-600 text-gray-300"
                          }`}
                        >
                          R{rev.revisionNumber}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-gray-200 truncate">
                              {rev.label || `Revision R${rev.revisionNumber}`}
                            </span>
                            {isActive && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-orange-500/20 text-orange-400 rounded-full border border-orange-500/40">
                                ✎ Saving here
                              </span>
                            )}
                            {isActive && isApproved && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-green-600/20 text-green-400 rounded-full border border-green-600/40">
                                <CheckCircleIcon className="h-3 w-3" /> Approved
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {formatDate(rev.createdAt)}
                          </div>
                          {rev.note && (
                            <div className="text-xs text-gray-400 mt-1 italic truncate max-w-xs">
                              "{rev.note}"
                            </div>
                          )}
                          <div className="flex gap-4 mt-1 text-xs">
                            <span className="text-green-400">
                              Cost: {formatCurrency(rev.totalBudgetCost)}
                            </span>
                            <span className="text-orange-400">
                              Price: {formatCurrency(rev.totalCustomerPrice)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => handleViewRevision(rev)}
                          disabled={loadingRevisionDetail}
                          className="px-2 py-1 text-xs text-gray-300 bg-gray-600/60 border border-gray-500 rounded hover:bg-gray-500/60 flex items-center gap-1"
                        >
                          <EyeIcon className="h-3 w-3" />
                          {loadingRevisionDetail ? "..." : "View"}
                        </button>
                        {!isApproved && !isActive && (
                          <button
                            type="button"
                            onClick={() => setRestoreTarget(rev)}
                            className="px-2 py-1 text-xs text-blue-400 bg-blue-500/10 border border-blue-500/30 rounded hover:bg-blue-500/20 flex items-center gap-1"
                          >
                            <ArrowPathIcon className="h-3 w-3" />
                            Restore
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
